import React, { useEffect, useState } from "react";
import { Edit } from "lucide-react";
import { useSelector } from "react-redux";
import {  GlobalService } from "../../../shared";
import Button from "../../../../components/ui/Button";
import Card from "../../../../components/ui/card";
import {Badge} from "../../../../components/ui/badge";
import NewCertificateModal from "../../modals/NewCertificateModal";
import { createUserCertificate, getUserCertificates, UpdateUserCertificate, useAppDispatch } from "../../../../redux";
import { useTranslation } from "react-i18next";

export default function Certificate({ userId, showEditIcon = true }: { userId?: string; showEditIcon?: boolean }) {
    const { t } = useTranslation();
    const { user } = useSelector((state: any) => state.auth);

    const [showCertificateModal, setShowCertificateModal] = useState(false);
    const dispatch = useAppDispatch();
    const [allCertificateData, setAllCertificateData] = useState([]);
    const [originalAllCertificateData, setOriginalAllCertificateData] = useState([]);
    const { certificates } = useSelector((state: any) => state.professional);
    const targetUserId = userId || user?.id; // Use passed userId or fallback to current user

    useEffect(() => {
        if (!certificates) return;
        const transformedCertificates = certificates.map((cert: any) => ({
            id: cert.id,
            name: cert.name,
            issuer: cert.issuer,
            issueDate: new Date(cert.issueDate).getFullYear().toString(),
            expiryDate: new Date(cert.expiryDate).getFullYear().toString(),
        }));        
        setOriginalAllCertificateData(transformedCertificates);
        setAllCertificateData(transformedCertificates);
    }, [certificates]);
    useEffect(() => {
        getAllCertificates();
    }, [targetUserId]);

    const getAllCertificates = async () => {
        await dispatch(getUserCertificates({ id: targetUserId }));
    };
    const handleCertificateSave = async (data: any[]) => {
        const newItems = data.filter(item => !item.id);
        const existingItems = data.filter(item => item.id);
        let updatedCount = 0;

        // Update changed existing items
        for (const item of existingItems) {
            const original = originalAllCertificateData.find(e => e.id === item.id);

            const hasChanged =
                item.name !== original?.name ||
                item.issuer !== original?.issuer ||
                item.issueDate !== original?.issueDate ||
                item.expiryDate !== original?.expiryDate;

            if (!hasChanged) continue;

            const updatePayload = {
                userId: { id: targetUserId },
                name: item.name,
                issuer: item.issuer,
                issueDate:item.issueDate,
                expiryDate:item.expiryDate,
            };

            await dispatch(UpdateUserCertificate({ id: item.id, data: updatePayload }));
            updatedCount++;
        }

        // Create new items
        let createdItems = [];
        if (newItems.length > 0) {
            const createPayload = newItems.map(item => ({
                userId: { id: targetUserId },
                name: item.name,
                issueDate:item.issueDate,
                issuer: item.issuer,
                expiryDate:item.expiryDate,
            }));            
            const result = await dispatch(createUserCertificate({ data: createPayload }));

          if (createUserCertificate.fulfilled.match(result)) {
    createdItems = result.payload.map((cert: any) => ({
        ...cert,
        issueDate: new Date(cert.issueDate).getFullYear().toString(),
        expiryDate: new Date(cert.expiryDate).getFullYear().toString(),
    }));
}
        }

        // Merge updated and newly created items
        const updatedData = [...existingItems, ...createdItems];

        setAllCertificateData(updatedData);
        setOriginalAllCertificateData(updatedData); // sync originals for future comparisons
        if (updatedCount > 0 && newItems.length === 0) {
            GlobalService.showToast("success", t("certificate.successTitle"), t("certificate.updateSuccess"));
        }
    };
    
    return (
        <>
            <Card color={undefined} className="p-6">
                <div className="flex justify-between items-start mb-4">
                    <h2 className="text-lg font-semibold">{t("certificate.title")}</h2>
                    {showEditIcon && (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="text-blue-600 hover:text-blue-700"
                            onClick={() => {
                                setShowCertificateModal(true);
                            }}>
                            <Edit className="w-4 h-4" />
                        </Button>
                    )}
                </div>
                <div className="space-y-4">
                    {allCertificateData.map((cert, index) => (
                        <div key={index} className="border rounded-lg p-4">
                            <h3 className="font-medium text-gray-900">{cert.name}</h3>
                            <p className="text-sm text-gray-600">{cert.issuer}</p>
                            <div className="flex justify-between items-center mt-2 text-sm text-gray-500">
                                <span>{t("certificate.issued")} {cert.issueDate}</span>
                                <span>{t("certificate.expires")} {cert.expiryDate}</span>
                            </div>
                        </div>
                    ))}
                </div>
            </Card>

            <NewCertificateModal
                open={showCertificateModal}
                onClose={() => {
                    setShowCertificateModal(false);
                }}
                onSave={handleCertificateSave}
                initialData={allCertificateData} // <-- edit mode
            />
        </>
    );
}
